import * as React from "react";
import { SingleNavMenuLink } from "../../../pages/identity/useNavMenu";
import { useNavigate } from "react-router-dom";
import { BasePathContext } from "../../../reactContexts/basePathContext";

export function useSingleNavMenuUpdateUrlWithScroll(_links: SingleNavMenuLink[], pagesWithScrolls: string[]) {
  const componentName = `"useSingleNavMenuUpdateUrlWithScroll"`;

  const basePath = React.useContext(BasePathContext);
  const navigate = useNavigate();

  const [foundElements, setFoundElements] = React.useState(false);
  const [queryCount, setQueryCount] = React.useState(0);
  const QUERY_COUNT_LIMIT = 5;

  const SCROLL_STOP_THRESHOLD_MS = 100;
  const lastScrollTimestamp = React.useRef(0);
  function getIsUserScrolling() {
    return new Date().getTime() - lastScrollTimestamp.current < SCROLL_STOP_THRESHOLD_MS;
  }
  React.useEffect(() => {
    const onWheel = () => {
      lastScrollTimestamp.current = new Date().getTime();
    };
    const onTouchmove = () => {
      lastScrollTimestamp.current = new Date().getTime();
    };
    const onScroll = () => {
      lastScrollTimestamp.current = new Date().getTime();
    };
    window.addEventListener("wheel", onWheel);
    window.addEventListener("touchmove", onTouchmove);
    window.addEventListener("scroll", onScroll);
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchmove", onTouchmove);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  React.useEffect(() => {
    if (queryCount >= QUERY_COUNT_LIMIT) {
      console.error(`Cannot process ${componentName}, query count is depleted without finding elements`);
      return;
    }

    const pageMain = document.querySelector(`#page main`) as HTMLElement;
    if (!pageMain) {
      setQueryCount((x) => x + 1);
      return;
    }

    setFoundElements(true);
  }, [foundElements, queryCount]);

  const links = React.useMemo(() => {
    return _links.filter((link) => (link as any).isSection);
  }, [_links]);

  const tryUpdateUrl = React.useCallback(() => {
    if (!getIsUserScrolling()) {
      return;
    }

    const container = document.querySelector(`#page main`) as HTMLElement;

    for (const link of links) {
      const linkPath = `${(link as any).page}/${(link as any).section}`;

      let isPartOfScrollablePages = false;
      for (const page of pagesWithScrolls) {
        if (linkPath.includes(page)) {
          isPartOfScrollablePages = true;
          break;
        }
      }
      if (!isPartOfScrollablePages) {
        return;
      }

      let calculatedPath = linkPath;
      for (const page of pagesWithScrolls) {
        calculatedPath = calculatedPath.replaceAll(`/${page}/`, "");
      }

      const targetParts = calculatedPath.split(`c_`);
      let sectionEl: HTMLElement = null as any;
      for (const targetPart of targetParts) {
        if (!targetPart) {
          continue;
        }
        const needsClick = calculatedPath.includes(`c_${targetPart}`);
        if (needsClick) {
          sectionEl = document.querySelector(`.cpln-form-section[data-section-id="${targetPart}"]`) as HTMLElement;
        } else {
          sectionEl = document.querySelector(`.cpln-form-section[data-section-id="${targetPart}"]`) as HTMLElement;
        }
      }

      if (sectionEl && container) {
        const rect = sectionEl.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const diff = containerRect.top - rect.top;
        const threshold = 50;
        if (rect.top > 0 && diff > -threshold && diff < threshold) {
          navigate(`${basePath}${linkPath}`, { replace: true });
        }
      }
    }
  }, []);

  React.useEffect(() => {
    if (!foundElements) {
      return;
    }

    const el = document.querySelector(`#page main`) as HTMLElement;
    if (!el) {
      console.error(
        `Cannot set active indicator in ${componentName} without being able to access element "#page main"`,
      );
      return;
    }

    tryUpdateUrl();
    el.addEventListener(`scroll`, tryUpdateUrl);
    return () => {
      el.removeEventListener(`scroll`, tryUpdateUrl);
    };
  }, [foundElements, links]);
}
